import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../firebase/config';

const SUSPENDED_MESSAGE =
  'Your account has been suspended. Please contact support@worldasia.co.uk for assistance.';

export interface AppUser {
  uid: string;
  email: string;
  displayName: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AppUser | null;
  userAccountType: 'customer' | 'vendor' | 'admin' | null;
  isModalOpen: boolean;
  modalMode: 'login' | 'signup' | 'vendor-signup';
  openAuthModal: (mode?: 'login' | 'signup' | 'vendor-signup') => void;
  closeAuthModal: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  signUpVendor: (email: string, password: string, firstName: string, lastName: string, businessName: string, businessType: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AccountType = AuthContextType['userAccountType'];

async function fetchUserProfile(userId: string) {
  const { data: userData } = await supabase
    .from('users')
    .select('account_type, is_active, email, display_name')
    .eq('id', userId)
    .maybeSingle();

  return {
    accountType: (userData?.account_type as AccountType) || 'customer',
    isActive: userData?.is_active !== false,
    email: userData?.email || '',
    displayName: userData?.display_name || '',
  };
}

async function ensureActiveAccount(userId: string): Promise<NonNullable<AccountType>> {
  const profile = await fetchUserProfile(userId);
  if (!profile.isActive) {
    await supabase.auth.signOut();
    throw new Error(SUSPENDED_MESSAGE);
  }
  return profile.accountType as NonNullable<AccountType>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [userAccountType, setUserAccountType] = useState<'customer' | 'vendor' | 'admin' | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'login' | 'signup' | 'vendor-signup'>('login');
  const [loading, setLoading] = useState(true);

  // Listen for auth state changes and fetch user data
  useEffect(() => {
    let mounted = true;

    const applySessionUser = async (authUser: SupabaseUser) => {
      const profile = await fetchUserProfile(authUser.id);
      if (!mounted) return;

      if (!profile.isActive) {
        await supabase.auth.signOut();
        setUser(null);
        setUserAccountType(null);
        return;
      }

      setUser({
        uid: authUser.id,
        email: profile.email || authUser.email || '',
        displayName:
          profile.displayName ||
          authUser.user_metadata?.display_name ||
          authUser.email ||
          '',
      });
      setUserAccountType(profile.accountType);
    };

    const bootstrap = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user && mounted) {
          await applySessionUser(session.user);
        }
      } catch (err) {
        console.error('Auth bootstrap failed:', err);
        if (mounted) {
          setUser(null);
          setUserAccountType(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, sessionUpdate) => {
      if (!mounted) return;

      const authUser = sessionUpdate?.user;
      if (authUser) {
        // Defer Supabase DB calls — awaiting inside this callback deadlocks auth (Strict Mode).
        setTimeout(() => {
          if (!mounted) return;
          applySessionUser(authUser).catch((err) =>
            console.error('Failed to fetch account type:', err)
          );
          if (event === 'USER_UPDATED' && authUser.email) {
            supabase
              .from('users')
              .update({
                email: authUser.email,
                updated_at: new Date().toISOString(),
              })
              .eq('id', authUser.id)
              .then(({ error }) => {
                if (error) console.warn('Failed to sync profile email:', error.message);
              });
          }
        }, 0);
      } else {
        setUser(null);
        setUserAccountType(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const openAuthModal = (mode: 'login' | 'signup' | 'vendor-signup' = 'login') => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsModalOpen(false);
  };

  const mapSignUpError = (error: any, fallback: string) => {
    const message = error?.message || '';
    switch (error?.code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled.';
      default:
        if (/row-level security/i.test(message)) {
          return 'Account setup failed. Please try again or contact support.';
        }
        return message || fallback;
    }
  };

  const registerViaApi = async (payload: Record<string, string>) => {
    const response = await fetch('/api/auth-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error || 'Sign up failed');
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: payload.email,
      password: payload.password,
    });
    if (signInError) throw signInError;
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) {
        await ensureActiveAccount(data.user.id);
      }
      closeAuthModal();
    } catch (error: any) {
      let errorMessage = 'An error occurred during sign in.';
      
      if (error.message === SUSPENDED_MESSAGE) {
        throw new Error(SUSPENDED_MESSAGE);
      }

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        default:
          errorMessage = error.message || 'An error occurred during sign in.';
      }
      
      throw new Error(errorMessage);
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      await registerViaApi({
        email,
        password,
        firstName,
        lastName,
        accountType: 'customer',
      });
      closeAuthModal();
    } catch (error: any) {
      throw new Error(mapSignUpError(error, 'An error occurred during sign up.'));
    }
  };

  const signUpVendor = async (email: string, password: string, firstName: string, lastName: string, businessName: string, businessType: string) => {
    try {
      await registerViaApi({
        email,
        password,
        firstName,
        lastName,
        accountType: 'vendor',
        businessName,
        businessType,
      });
      closeAuthModal();
    } catch (error: any) {
      throw new Error(mapSignUpError(error, 'An error occurred during vendor sign up.'));
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
      closeAuthModal();
    } catch (error: any) {
      let errorMessage = 'An error occurred during Google sign in.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in was cancelled.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Sign in popup was blocked. Please allow popups for this site.';
      } else {
        errorMessage = error.message || 'An error occurred during Google sign in.';
      }
      
      throw new Error(errorMessage);
    }
  };

  const signInWithFacebook = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
      closeAuthModal();
    } catch (error: any) {
      let errorMessage = 'An error occurred during Facebook sign in.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in was cancelled.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Sign in popup was blocked. Please allow popups for this site.';
      } else {
        errorMessage = error.message || 'An error occurred during Facebook sign in.';
      }
      
      throw new Error(errorMessage);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw new Error('An error occurred during sign out.');
    }
  };

  const value: AuthContextType = {
    isAuthenticated: !!user,
    user,
    userAccountType,
    isModalOpen,
    modalMode,
    openAuthModal,
    closeAuthModal,
    signIn,
    signUp,
    signUpVendor,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 